// src/components/Estadisticas/EstadisticasCRM.js
import {
	Box,
	Flex,
	Heading,
	SimpleGrid,
	Stack,
	Stat,
	StatLabel,
	StatNumber,
	Text,
	useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getEstadisticasCRM } from '../../services/estadisticas';
import Layout from '../../components/Layout/Layout';
import ActividadesMensualesChart from '../../components/Estadisticas/ActividadesMesualesChart';
import ActividadesResponsable from '../../components/Estadisticas/ActividadesPorResponsable';
import ActividadesTipo from '../../components/Estadisticas/ActividadesTipo';
import FiltrosEstadisticas from '../../components/Filtros/FiltroEstadisticas';
import InmueblesPorResponsableChart from '../../components/Estadisticas/InmueblePorResponsableChart';
import NoticiasPorResponsableChart from '../../components/Estadisticas/NoticiasPorResponsableChart';
import EstadisticasPorTrabajador from '../../components/Estadisticas/EstadisticasPorTrabajador';
import EstadisticasPorAgencia from '../../components/Estadisticas/EstadisticasPorAgencia';
import CitasPorResponsableChart from '../../components/Estadisticas/CitasPorResponsable';

const etiquetasLegibles = {
	inmuebleMercado: 'Inmueble en mercado',
	cerradaSinExito: 'Cerrada sin éxito',
	cerradaConExito: 'Cerrada con éxito',
	enProceso: 'En proceso',
	cerrados: 'Cerrados',
	cancelados: 'Cancelados',
	activos: 'Activos',
	cerradosNegativamente: 'Cerrados sin éxito',
	// Puedes añadir más según vayan saliendo
};

const generarColores = cantidad => {
	const colores = [];
	const hueStep = 137.508; // número de oro multiplicado por 360

	for (let i = 0; i < cantidad; i++) {
		const hue = (i * hueStep) % 360;
		colores.push(`hsl(${hue}, 70%, 50%)`);
	}

	return colores;
};

const EstadisticasCRM = () => {
	const [filters, setFilters] = useState({
		tipo: 'general',
		ultimosDias: '',
	});

	const [data, setData] = useState({
		totales: {
			actividades: 0,
			pedidos: 0,
			encargos: 0,
			noticias: 0,
			inmuebles: 0,
		},
		actividadesPorTipo: [],
		actividadesMensuales: [],
		actividadesPorResponsable: [],
		inmueblesPorResponsable: [],
		noticiasPorResponsable: [],
		citasPorResponsable: [],
	});

	// total citas realizadas
	const total = data.citasPorResponsable?.reduce(
		(acc, item) => acc + item.total,
		0,
	);
	const totalFijadas = data.citasPorResponsable?.reduce(
		(acc, item) => acc + item.citas_fijadas,
		0,
	);
	const totalRealizadas = data.citasPorResponsable?.reduce(
		(acc, item) => acc + item.citas_realizadas,
		0,
	);

	const toast = useToast();

	const fetchEstadisticas = useCallback(async () => {
		try {
			const { tipo, ultimosDias } = filters;
			const params = {};

			if (ultimosDias) params.ultimosDias = ultimosDias;
			if (tipo) params.tipo = tipo;
			if (tipo === 'general') {
				const datos = await getEstadisticasCRM(params);
				setData(datos);
			}
		} catch (error) {
			toast({
				title: 'Error al cargar estadísticas',
				status: 'error',
				isClosable: true,
			});
		}
	}, [filters, toast]);

	useEffect(() => {
		fetchEstadisticas();
	}, [fetchEstadisticas]);

	const handleFilterChange = e => {
		setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleTipoChange = value => {
		setFilters(prev => ({ ...prev, tipo: value }));
	};

	const responsables = data.actividadesPorResponsable
		.map(r => {
			const palabras =
				typeof r.nombre === 'string' ? r.nombre.trim().split(' ') : [];
			return {
				nombre: palabras.slice(0, 2).join(' '), // se usa como label visible
				nombreCompleto: r.nombre, // se usa en el tooltip
				total: Number(r.total),
			};
		})
		.filter(r => !isNaN(r.total) && r.total > 0);

	const altoGrafico = Math.max(responsables.length * 45, 300); // mínimo 300px

	const coloresUnicos = useMemo(
		() => generarColores(data.actividadesPorTipo.length),
		[data.actividadesPorTipo.length],
	);

	return (
		<Layout>
			{/* <VStack spacing={10} w='100%' px={8} py={6} align='start'> */}
			<Box mb={4}>
				<Heading textAlign='center' color='blue.800'>
					ESTADÍSTICAS
				</Heading>
			</Box>

			{/* Filtros */}
			<FiltrosEstadisticas
				filters={filters}
				handleFilterChange={handleFilterChange}
				handleTipoChange={handleTipoChange}
				handleAplicarFiltro={fetchEstadisticas}
			/>

			{/* Estadísticas por trabajador */}
			{filters.tipo === 'trabajadores' && (
				<EstadisticasPorTrabajador filters={filters} />
			)}

			{filters.tipo === 'agencias' && (
				<EstadisticasPorAgencia filters={filters} />
			)}

			{/* Estadísticas generales */}
			{filters.tipo === 'general' && (
				<Box
					mt={6}
					w='100%'
					border='1px'
					borderColor='blue.400'
					rounded='lg'
					shadow='lg'
					p={4}
				>
					{/* Totales */}
					<SimpleGrid columns={[1, 2, 3]} spacing={6} w='100%'>
						{Object.entries(data.totales).map(([key, val]) => (
							<Stat
								key={key}
								p={4}
								borderWidth='1px'
								rounded='md'
							>
								<StatNumber fontSize='lg'>
									{typeof val === 'object'
										? (val.total ?? 0)
										: val}
								</StatNumber>
								<StatLabel
									fontWeight='bold'
									textTransform='capitalize'
									color={'blue.600'}
									fontSize={'sm'}
								>
									{key}
								</StatLabel>

								{typeof val === 'object' &&
									Object.entries(val).map(
										([subKey, subVal]) => {
											if (subKey === 'total') return null; // ya mostrado arriba
											return (
												<Text
													fontSize='sm'
													key={subKey}
												>
													{etiquetasLegibles[
														subKey
													] || subKey}
													: <b>{subVal}</b>
												</Text>
											);
										},
									)}
							</Stat>
						))}
					</SimpleGrid>
					{/* Datos por responsable */}
					{data.inmueblesPorResponsable.length > 0 && (
						<Box
							w='100%'
							mt={4}
							borderColor='blue.400'
							rounded='lg'
							shadow='md'
							p={4}
						>
							<InmueblesPorResponsableChart
								data={data.inmueblesPorResponsable}
							/>
						</Box>
					)}
					{data.noticiasPorResponsable.length > 0 && (
						<Box
							w='100%'
							mt={4}
							borderColor='blue.400'
							rounded='lg'
							shadow='md'
							p={4}
						>
							{data.noticiasPorResponsable.length > 0 && (
								<NoticiasPorResponsableChart
									data={data.noticiasPorResponsable}
								/>
							)}
						</Box>
					)}
					{data.citasPorResponsable?.length > 0 && (
						<Flex>
							<Box
								w='100%'
								mt={4}
								borderColor='blue.400'
								rounded='lg'
								shadow='md'
								p={4}
							>
								<CitasPorResponsableChart
									data={data.citasPorResponsable}
								/>
							</Box>

							<Box
								minW='220px'
								p={4}
								border='1px solid'
								borderColor='gray.200'
								rounded='md'
								bg='gray.50'
							>
								<Heading size='sm' mb={3}>
									Resumen
								</Heading>
								<Stack spacing={1}>
									<Text>
										<strong>Total citas:</strong> {total}
									</Text>
									<Text color='blue.600'>
										<strong>Fijadas:</strong> {totalFijadas}
									</Text>
									<Text color='green.600'>
										<strong>Realizadas:</strong>{' '}
										{totalRealizadas}
									</Text>
								</Stack>
							</Box>
						</Flex>
					)}

					{/* Actividades por tipo */}
					<Box
						w='100%'
						mt={4}
						borderColor='blue.400'
						rounded='lg'
						shadow='md'
						p={4}
					>
						<Heading size='md' mb={4}>
							Actividades por tipo
						</Heading>
						<ActividadesTipo
							data={data}
							coloresUnicos={coloresUnicos}
						/>
					</Box>
					{/* Actividades mensuales */}
					<Box
						w='100%'
						mt={4}
						borderColor='blue.400'
						rounded='lg'
						shadow='md'
						p={4}
					>
						<Heading size='md' mb={2}>
							Evolución mensual de actividades
						</Heading>
						<ActividadesMensualesChart
							data={data.actividadesMensuales}
						/>
					</Box>
					{/* Actividades por responsable */}
					<Box
						w='100%'
						mt={4}
						borderColor='blue.400'
						rounded='lg'
						shadow='md'
						p={4}
					>
						<Heading size='md' mb={2}>
							Actividades por responsable
						</Heading>
						<ActividadesResponsable
							data={responsables}
							altoGrafico={altoGrafico}
						/>
					</Box>
				</Box>
			)}

			{/* Si hay algún error, mostrarlo */}
			{data.error && (
				<Box
					mt={6}
					p={4}
					borderWidth='1px'
					borderColor='red.400'
					rounded='md'
					bg='red.50'
				>
					<Text color='red.800'>
						Error al cargar las estadísticas: {data.error}
					</Text>
				</Box>
			)}
		</Layout>
	);
};

export default EstadisticasCRM;
